import React from "react";
import dynamic from "next/dynamic";
import {
    AVAILABILITY_CHOICE,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import commonStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import CheckoutAccordion from "../../../../../CheckoutAccordion/CheckoutAccordion";
import SessionDetailsFormGroupHeader from "./modules/SessionDetailsFormGroupHeader/SessionDetailsFormGroupHeader";
import { IFormGroupWrapperProps } from "../../../../../CheckoutAccordion/CheckoutAccordion.interfaces";
import { checkout_form } from "../../../CheckoutForm/CheckoutForm.constants";
import { useSessionDetailsFormGroup } from "./SessionDetailsFormGroup.utils";
import ClassesSessions from "./modules/ClassesSessions/ClassesSessions";
import SessionInformationComponent from "./modules/SessionInformationComponent/SessionInformationComponent";
import styles from "./SessionDetailsFormGroup.module.scss";

// Lazy Loading these component, as in a given listing, only one component is required
// based upon the listing type
const WebinarSessions = dynamic(
    () => import("./modules/WebinarSessions/WebinarSessions")
);
const WorkshopSessions = dynamic(
    () => import("./modules/WorkshopSessions/WorkshopSessions")
);
const AppointmentsSlots = dynamic(
    () => import("./modules/AppointmentsSlots/AppointmentsSlots")
);

const Root = ({
    children,
    classesCount,
}: {
    children: IFormGroupWrapperProps["children"];
    classesCount: number;
}) => (
    <CheckoutAccordion
        showExpandIcon={false}
        title={
            <SessionDetailsFormGroupHeader
                timezoneFieldName={
                    checkout_form.timezone_details.field_names.timezone_data
                }
                classesCount={classesCount}
            />
        }
        className={commonStyles.Wrapper}
        expanded
        classes={{
            root: commonStyles.formGroupRoot,
            disabled: commonStyles.formGroupDisabled,
        }}
        headerClasses={{
            root: commonStyles.headerRoot,
            content: styles.headerContent,
            disabled: commonStyles.formGroupDisabled,
            expanded: commonStyles.formGroupExpanded,
        }}
    >
        {children}
    </CheckoutAccordion>
);

const SessionDetailsFormGroup = () => {
    const {
        isEmptySlots,
        isEmptyClasses,
        slots,
        slotsCount,
        classes,
        classesCount,
        startDate = "",
        endDate = "",
        maxSpots = 10000,
    } = useSessionDetailsFormGroup();

    const { listingType, availabilityChoice } =
        useCheckoutBasicDetailsContext();

    let SessionSelectorComponent: JSX.Element;

    switch (listingType) {
        case LISTING_TYPES.WEBINAR: {
            if (isEmptySlots) return null;
            SessionSelectorComponent = (
                <WebinarSessions
                    hide={isEmptySlots}
                    sessions={slots}
                    sessionsCount={slotsCount}
                />
            );
            break;
        }
        case LISTING_TYPES.WORKSHOPS: {
            if (isEmptySlots) return null;
            SessionSelectorComponent = (
                <WorkshopSessions
                    hide={isEmptySlots}
                    sessions={slots}
                    sessionsCount={slotsCount}
                />
            );
            break;
        }
        case LISTING_TYPES.APPOINTMENTS: {
            const isAfterBookingSlotsShare =
                availabilityChoice === AVAILABILITY_CHOICE.NOT_DECIDED;
            if (isAfterBookingSlotsShare) {
                return (
                    <SessionInformationComponent value="The schedule for this appointment will be shared with you after booking." />
                );
            }
            if (isEmptySlots) {
                return null;
            }
            SessionSelectorComponent = (
                <AppointmentsSlots
                    slots={slots}
                    startDate={startDate}
                    endDate={endDate}
                />
            );

            break;
        }
        case LISTING_TYPES.ROLLING_CLASSES:
        case LISTING_TYPES.GROUP_CLASSES:
        case LISTING_TYPES.ONE_ON_ONE_CLASSES: {
            if (isEmptyClasses) return null;
            SessionSelectorComponent = (
                <ClassesSessions
                    hide={isEmptyClasses}
                    classes={classes}
                    classesCount={classesCount}
                    maxSpots={maxSpots}
                />
            );

            break;
        }

        default:
            return null;
    }

    if (!SessionSelectorComponent) {
        return null;
    }
    return <Root classesCount={classesCount}>{SessionSelectorComponent}</Root>;
};

export default SessionDetailsFormGroup;
